/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { stringify } from "query-string";
import { generatePath } from "react-router-dom";
import {
  ChartTableSortField,
  KeyValues,
  Provider,
  SearchPresetFilter,
} from "~/generated/graphql";
import {
  CostAnalysisFields,
  Period,
  SortDirection,
  TableData,
} from "~/components";
import { BaseResource } from "~/components/EstateRecordsList";
import { EstatePeriod } from "~/constants";
import { ROUTES } from "~/routing/routes";
import {
  formatMoneyWithFallback,
  getFirstItem,
  isEmpty,
  isNil,
  isNotNil,
  Maybe,
} from "~/tools";
import { Resource as ResourceType } from "./types";

export function generateRowData(res: BaseResource, currency: string) {
  const baseData = {
    id: res?.id,
    provider: res?.provider,
    type: res?.providerType,
    nordcloudId: res?.id,
    cloudAccountId: "",
    nid: "",
    name: "",
    application: "",
    environment: null,
    actualCosts: undefined,
    forecast: "Unavailable",
    region: "global",
    isWaste: false,
    reason: undefined,
    savingsAmount: undefined,
    savingsAmountCount: undefined,
    co2e: undefined,
    co2eSavings: undefined,
    resourceConsumption: undefined,
    metadataAndTags: undefined,
    count: undefined,
    environments: undefined,
  };

  if (res?.__typename === "CloudResource") {
    return {
      ...baseData,
      cloudAccountId: res?.cloudAccountId,
      providerId: res?.providerId,
      nid: res?.environment?.nid,
      name: res?.name,
      category: res?.category,
      application: res?.environment?.application?.name,
      environment: res?.environment,
      actualCosts: formatMoneyWithFallback(res?.searchCost, currency),
      forecast: formatMoneyWithFallback(
        res?.cost?.forecast,
        currency,
        "Unavailable"
      ),
      region: res?.region || "global",
      isWaste: res?.cloudWaste?.isWaste || false,
      reason: res?.cloudWaste?.reason ?? undefined,
      savingsAmount: res?.savingsAmount?.amount ?? undefined,
      savingsAmountCount: res?.savingsAmount?.count ?? undefined,
      co2e: res?.co2e?.currentMonth,
      co2eSavings: res?.savingsAmount?.co2e ?? undefined,
      resourceConsumption: res?.resourceConsumption ?? undefined,
      metadataAndTags: res?.metadataAndTags ?? undefined,
      count: res.cost?.count ?? undefined,
      assignments: res?.assignments,
      environments: res?.environmentsPaginated?.environments ?? undefined,
      environmentsPaginated: res?.environmentsPaginated ?? undefined,
    };
  }

  if (res?.__typename === "CostResource") {
    return {
      ...baseData,
      name: res?.providerType,
      assignments: res?.assignments,
    };
  }

  return baseData;
}

export function generateNewRowData(res: BaseResource, currency: string) {
  const baseData = generateRowData(res, currency);

  if (res?.__typename === "CostResource") {
    return {
      ...baseData,
      name: res?.providerType,
      actualCosts: formatMoneyWithFallback(res?.searchCost, currency),
      // Temporary solution to set "Unavailable" until we haven't implemented forecast from new search
      forecast: "Unavailable",
    };
  }

  return baseData;
}

export function convertTags(tags?: string[]) {
  return tags?.map((tag) => {
    const keyValue = tag.split(",");
    const value =
      isNil(keyValue[1]) || isEmpty(keyValue[1]) ? [] : [keyValue[1]];
    return { key: keyValue[0] ?? "", values: value };
  });
}

type OmitByValue<T, ValueType> = Pick<
  T,
  { [Key in keyof T]-?: T[Key] extends ValueType ? never : Key }[keyof T]
>;

export function filterEmptyValuesInObject<T extends { [K in keyof T]?: T[K] }>(
  input: T
): OmitByValue<T, null | undefined> {
  return Object.fromEntries(
    Object.entries(input).filter(([_, value]) => isNotNil(value))
  ) as T;
}

export function mapSearchPresetFilterToFilter(savedFilter: SearchPresetFilter) {
  return {
    ...savedFilter,
    tags: savedFilter.tags?.map(mapKeyValueToQueryString),
    metadata: savedFilter.metadata?.map(mapKeyValueToQueryString),
  };
}

function mapKeyValueToQueryString({ key, values }: KeyValues) {
  return `${key},${getFirstItem(values) ?? ""}`;
}

export function isGreaterThan(target: Maybe<number>, value: number) {
  return target && target > value;
}

export function getResources(
  resource: Maybe<{ id: string; provider: Provider }>
): ResourceType[] {
  return [
    resource != null
      ? { nid: resource.id, provider: resource.provider }
      : undefined,
  ].filter(isNotNil);
}

export function getMultiResources(
  selectedResources: string[],
  resources: BaseResource[] | null | undefined
) {
  return selectedResources
    .map((nid) => {
      const provider = resources?.find(({ id }) => nid === id)?.provider;

      if (provider) {
        return {
          nid,
          provider,
        };
      }
      return undefined;
    })
    .filter(isNotNil);
}

export function getUrl(id: string, nid: string, subType: string) {
  if (nid) {
    return generatePath(`${ROUTES.resource}`, { nordcloudId: nid });
  }

  return `${ROUTES.estateRecords.index}?${stringify(
    {
      query: id,
      typeSubtype: [subType],
    },
    { arrayFormat: "index" }
  )}`;
}

export function mapEstatePeriodToPeriod(estatePeriod: EstatePeriod): Period {
  if (estatePeriod === EstatePeriod.RANGE) {
    return Period.RANGE;
  }

  return Period.MONTH;
}

export function sortDataByTotal(
  data: TableData[],
  sortDirection: SortDirection
) {
  const totalRecord = data.find(
    (value) => value.id === CostAnalysisFields.TOTAL_COST
  );
  const dataToSort = data.filter(
    (value) => value.id !== CostAnalysisFields.TOTAL_COST
  );

  if (sortDirection === SortDirection.Descending) {
    const dataDesc = [...dataToSort].sort((n1, n2) =>
      n1.total > n2.total ? -1 : 1
    );

    return [totalRecord, ...dataDesc].filter(isNotNil);
  }

  const dataAsc = [...dataToSort].sort((n1, n2) =>
    n1.total > n2.total ? 1 : -1
  );
  return [totalRecord, ...dataAsc].filter(isNotNil);
}

export function isTableDataSortable(
  sortField: ChartTableSortField,
  period: EstatePeriod
) {
  return (
    sortField === ChartTableSortField.Cost && period === EstatePeriod.RANGE
  );
}
